import { createContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router";
import { datadogRum } from "@datadog/browser-rum";
import { ClientError } from "graphql-request";
import Alert from "components/shared/Alert";
import useProjectData from "hooks/api/GQL/project/useProjectData";
import { IProjectData } from "hooks/api/GQL/project/useProjectData.models";
import useARNTenant from "hooks/useARNTenant";
import { IOrganization } from "models/organizations.models";
import useProjectStatusOptions, {
  TProjectStatusOption,
} from "screens/Project/hooks/useProjectStatusOptions/useProjectStatusOptions";
import { DatadogCustomErrors } from "utils/constants/common";
import { handleError, LayoutError } from "utils/helpers";

export type TProjectContextValue<T> = {
  project: IProjectData<T> | null | undefined;
  isProjectLoading: boolean;
  isProjectFetching: boolean;
  creatorTenant: IOrganization | undefined;
  projectStatusOptions: TProjectStatusOption[];
  projectError: ClientError | null;
};

export const ProjectContext = createContext<
  TProjectContextValue<object> | undefined
>(undefined);

const ProjectProvider = () => {
  const { t } = useTranslation("common");

  const { projectId } = useParams();

  const {
    data: project,
    isLoading: isProjectLoading,
    isFetching: isProjectFetching,
    error: projectError,
  } = useProjectData(projectId);

  if (project === null) {
    const errorMessage = t("messages.doesNotExistOrYouDoNotHaveAccess");

    datadogRum.addError(
      new Error(`${DatadogCustomErrors.PROJECT}: ${errorMessage}`),
    );

    handleError({
      error: new LayoutError({
        messageTranslationKey: "doesNotExistOrYouDoNotHaveAccess",
      }),
    });
  }

  const { tenant } = useARNTenant(project?.sourceArn ?? project?.id);

  const projectStatusOptions = useProjectStatusOptions(project);

  const value = useMemo(
    () => ({
      project,
      isProjectLoading,
      isProjectFetching,
      creatorTenant: tenant,
      projectStatusOptions,
      projectError,
    }),
    [
      project,
      isProjectLoading,
      isProjectFetching,
      tenant,
      projectStatusOptions,
      projectError,
    ],
  );

  return (
    <ProjectContext.Provider value={value}>
      {project?.active === false && (
        <Alert severity="info" square>
          {t("messages.viewOnlyProjectModeAlert")}
        </Alert>
      )}
      <Outlet />
    </ProjectContext.Provider>
  );
};

export default ProjectProvider;
